<script setup lang="ts">
import { BaseSerializedChart, SerializedRegionSumChart } from '@/composables/charts/influx-charts/chart-builders/ChartInterfaces'
import { SerializedGeneralChartTimeSetup } from '@/composables/charts/influx-charts/chart-serialization/ChartTimeSetup'
import { computed, ComputedRef, onMounted, ref, Ref, watch } from 'vue'
import { TagSelector, xAggregations } from '@/composables/charts/influx-charts/query-builders/InfluxTypes'
import { useMeasurementStore } from '@/store/measurements'
import { CHART_TYPES } from '@/composables/charts/influx-charts/chart-builders/CHART_TYPES'
import XAggregationPicker from '@/components/charts/chart-creator/XAggregationPicker.vue'
import _ from 'lodash'
import { useDisplay } from 'vuetify'

const props = defineProps<{ baseSerialized: BaseSerializedChart | null; timeSetup: SerializedGeneralChartTimeSetup | null }>()
const model = defineModel<SerializedRegionSumChart | null>()

const { mobile } = useDisplay()

const pickedRegionSumProperty: Ref<string> = ref('stallbereich_vh')

const useAllRegionSumValues: Ref<boolean> = ref(true)
const allowEmptyRegionSumValues: Ref<boolean> = ref(false)
const specificRegionSumValues: Ref<string[]> = ref([])
const availableFields: Ref<string[]> = ref([])
const pickedField: Ref<string> = ref('')
const xAggregation: Ref<xAggregations> = ref('mean')
const availableTagValuesForProperty: Ref<string[]> = ref([])
const availableTagValuesForValue: Ref<string[]> = ref([])
const availableTagKeys: Ref<string[]> = ref([])

const compiledModel: ComputedRef<SerializedRegionSumChart | null> = computed(() => {
  if (!pickedField.value) return null
  if (!props.baseSerialized) return null
  if (!props.timeSetup) return null
  return {
    ...props.baseSerialized,
    field: pickedField.value,
    regionSumValueTagSelector: regionSumValueTagSelector.value,
    timeSetup: props.timeSetup,
    chartType: CHART_TYPES.REGION_SUMS,
    xAggregation: xAggregation.value,
    regionSumProperty: pickedRegionSumProperty.value,
  }
})

const regionSumValueTagSelector: ComputedRef<TagSelector<'value'>> = computed(() => {
  return {
    tag: 'value',
    useAll: useAllRegionSumValues.value,
    groupByValues: true,
    allowEmptyValues: allowEmptyRegionSumValues.value,
    ...(!useAllRegionSumValues.value && specificRegionSumValues.value.length && { specificValues: specificRegionSumValues.value }),
  }
})

async function loadAvailableTagValues() {
  availableTagValuesForProperty.value = await useMeasurementStore().loadAvailableTagValuesForMeasurement(undefined, 'regionSums', 'property')
  availableTagValuesForValue.value = await useMeasurementStore().loadAvailableTagValuesForMeasurement(undefined, 'regionSums', 'value')
}

async function loadAvailableFields() {
  availableFields.value = await useMeasurementStore().loadAvailableFieldsForMeasurement(undefined, 'regionSums')
  if (!availableFields.value.includes(pickedField.value)) pickedField.value = availableFields.value[0]
}

async function loadAvailableTagKeys() {
  availableTagKeys.value = await useMeasurementStore().loadAvailableTagKeyForMeasurement(undefined, 'regionSums')
}

onMounted(async () => {
  await loadAvailableTagKeys()
  await loadAvailableFields()
})

watch(
  model,
  () => {
    if (!model.value) return
    if (_.isEqual(compiledModel.value, model.value)) return
    pickedRegionSumProperty.value = model.value.regionSumProperty
    xAggregation.value = model.value.xAggregation
    useAllRegionSumValues.value = model.value.regionSumValueTagSelector.useAll
    allowEmptyRegionSumValues.value = model.value.regionSumValueTagSelector.allowEmptyValues
    specificRegionSumValues.value = model.value.regionSumValueTagSelector.specificValues || []
    pickedField.value = model.value.field || availableFields.value[0]
  },
  { deep: true, immediate: true }
)

watch(
  compiledModel,
  () => {
    if (!compiledModel.value) return
    if (_.isEqual(compiledModel.value, model.value)) return
    model.value = compiledModel.value
  },
  { immediate: true, deep: true }
)

watch(
  pickedField,
  async () => {
    await loadAvailableTagValues()
  },
  { immediate: true }
)
</script>

<template>
  <v-layout-card
    class="pa-0"
    variant="flat"
    elevation="0"
  >
    <v-card-title class="pt-0">
      <v-icon
        icon="mdi-vector-square-plus"
        start
      />
      {{ $t('chart_sharing.creator.customize.region_sums.title') }}
    </v-card-title>
    <v-card-text
      class="py-2"
      :class="{ 'px-0': mobile, 'px-6': !mobile }"
    >
      <x-aggregation-picker v-model="xAggregation" />

      <v-autocomplete
        v-model="pickedRegionSumProperty"
        prepend-icon="mdi-vector-point-plus"
        variant="outlined"
        :items="availableTagValuesForProperty"
        :label="$t('chart_sharing.creator.customize.region_sums.property')"
      />

      <v-autocomplete
        v-model="pickedField"
        prepend-icon="mdi-grid"
        variant="outlined"
        :items="availableFields"
        :label="$t('chart_sharing.creator.customize.region_sums.field')"
      />

      <v-switch
        v-model="allowEmptyRegionSumValues"
        :label="$t('chart_sharing.creator.customize.region_sums.allow_empty')"
        hide-details
      />
      <v-switch
        v-model="useAllRegionSumValues"
        :label="$t('chart_sharing.creator.customize.region_sums.use_all')"
        hide-details
      />
      <v-fade-transition>
        <v-autocomplete
          v-if="!useAllRegionSumValues"
          v-model="specificRegionSumValues"
          prepend-icon="mdi-tag-multiple"
          variant="outlined"
          hide-details
          chips
          closable-chips
          :label="$t('chart_sharing.creator.customize.region_sums.specific_values')"
          :items="availableTagValuesForValue"
          multiple
          :disabled="useAllRegionSumValues"
        />
      </v-fade-transition>
    </v-card-text>
  </v-layout-card>
</template>
